import React, { useState } from 'react';
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    LinearProgress,
    Skeleton,
    Typography,
} from '@mui/material';
import { Activity, ChevronDown, ChevronUp, Database, HardDrive } from 'lucide-react';
import GlassPaper from '../../../components/GlassPaper';
import FieldLabel from './FieldLabel';
import { useTickTelemetry } from '../api';

function MetricCard({ title, icon, children }) {
    return (
        <Box
            sx={{
                flex: '1 1 180px',
                minWidth: 160,
                p: 1.25,
                borderRadius: '4px',
                bgcolor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(30,41,59,0.45)' : 'rgba(241,245,249,0.65)',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                {icon}
                <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Box>
    );
}

export default function TelemetryPanel() {
    const { data, isLoading, isError, error } = useTickTelemetry();
    const [open, setOpen] = useState(true);
    const [showDetails, setShowDetails] = useState(false);

    if (isLoading) {
        return <Skeleton variant="rounded" height={56} sx={{ mb: 2, borderRadius: 1 }} />;
    }

    if (isError) {
        return (
            <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: 'error.dark', color: 'error.contrastText' }}>
                <Typography variant="body2">Tick Lake API unavailable: {error.message}</Typography>
            </Box>
        );
    }

    const disk = data.disk || {};
    const usedPct = disk.total_gb ? (disk.used_gb / disk.total_gb) * 100 : 0;

    return (
        <GlassPaper sx={{ mb: 2, p: 1.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Activity size={18} />
                    <Typography variant="subtitle2" fontWeight={600}>
                        Tick Lake
                    </Typography>
                    <Chip label="API ok" size="small" color="success" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                        {(data.date_count || 0).toLocaleString()} trading days
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Toggle telemetry">
                    {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </IconButton>
            </Box>

            <Collapse in={open}>
                <Box display="flex" gap={1.5} mt={1.5} flexWrap="wrap">
                    <MetricCard title="Coverage" icon={<Database size={14} />}>
                        <Typography variant="body2" fontWeight={600}>
                            {data.earliest_date || '—'} → {data.latest_date || '—'}
                        </Typography>
                        <Chip size="small" label={data.timezone} sx={{ mt: 0.75 }} />
                    </MetricCard>

                    <MetricCard title="Disk" icon={<HardDrive size={14} />}>
                        <LinearProgress
                            variant="determinate"
                            value={usedPct}
                            sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {disk.free_gb ?? '—'} GB free
                            {disk.used_gb != null && disk.total_gb != null
                                ? ` · ${disk.used_gb} / ${disk.total_gb} GB`
                                : ''}
                        </Typography>
                    </MetricCard>
                </Box>

                <Box mt={1}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        {showDetails ? 'Hide' : 'Show'} infrastructure details
                    </Typography>
                    <Collapse in={showDetails}>
                        <Box mt={0.75}>
                            <FieldLabel label="Parquet root" />
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {data.parquet_root}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                API proxy: /api/tick-lake → Historical Tick Lake FastAPI (Mac mini :8091)
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>
            </Collapse>
        </GlassPaper>
    );
}
